/**
 * Le module **Paramètres** — les valeurs métier, leurs bornes, et les drapeaux
 * de produit (`EX-GEN-008`, `EX-REG-006`, écran `B-28`).
 *
 * Maquette : artboard `b_parametres`.
 *
 * ## LE BOUTON QUI RENDAIT 200 SANS RIEN FAIRE
 *
 * C'est le point de cet écran, et c'est pourquoi il n'a pas pu être livré plus
 * tôt. `FEATURE_FLAG_UPDATE` existait dans la matrice depuis la `V7`, **en
 * double validation**. La table des drapeaux existait. Le dépôt qui les écrit
 * existait. Et **aucun exécuteur n'était raccordé** : l'action retombait sur
 * `RecordedActionExecutor`, qui n'exécute rien et le dit.
 *
 * Deux personnes signaient, le journal d'audit enregistrait la décision, le
 * socle rendait 200 — et aucun produit ne s'ouvrait. Un écran qui aurait câblé
 * ce bouton aurait menti à son utilisateur **avec la caution d'une double
 * signature**. `FeatureFlagUpdateExecutor` a donc été écrit d'abord.
 *
 * ## UN PRODUIT « OUVERT » À 3 % N'EST PAS UN PRODUIT OUVERT
 *
 * `rolloutPercentage` accompagne toujours `enabled`. L'afficher sans sa part
 * ferait croire à une mise en service générale là où trois clients sur cent la
 * reçoivent.
 *
 * ## LES BORNES NE SE SUPPOSENT PAS
 *
 * `minValue` et `maxValue` peuvent manquer, et ce n'est pas zéro : un paramètre
 * sans borne n'est pas un paramètre borné à zéro. C'est sur ces bornes qu'un
 * exploitant décide s'il peut changer une valeur sans risque.
 *
 * ## CE QUI N'EST PAS OFFERT, ET POURQUOI
 *
 * **Modifier un paramètre n'est pas proposé.** Aucune action de la matrice ne
 * le porte — `SYSTEM_PARAMETER_VIEW` est une lecture, et il n'existe pas de
 * `SYSTEM_PARAMETER_UPDATE`. Offrir un champ éditable ouvrirait un geste que le
 * socle refuserait, et le refus se lirait comme un défaut de droit.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import {
  fetchFeatureFlags,
  fetchSystemParameters,
  switchFeatureFlag,
} from '../http/administration.js';
import type { FeatureFlag, SystemParameter } from '../http/administration.js';

/** Le module Paramètres. */
export function SystemSettingsScreen(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();

  const [parametres, setParametres] = useState<readonly SystemParameter[] | null>(null);
  const [erreurParametres, setErreurParametres] = useState<string | null>(null);
  const [drapeaux, setDrapeaux] = useState<readonly FeatureFlag[] | null>(null);
  const [erreurDrapeaux, setErreurDrapeaux] = useState<string | null>(null);
  const [bascule, setBascule] = useState<FeatureFlag | null>(null);
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [domaine, setDomaine] = useState<string | null>(null);

  const relire = useCallback(async (): Promise<void> => {
    const [valeurs, produits] = await Promise.all([
      fetchSystemParameters(client).then(
        (liste) => ({ liste, erreur: null as string | null }),
        (echec: unknown) => ({
          liste: null,
          erreur: echec instanceof ApiError ? echec.messageKey : 'errors.server',
        }),
      ),
      fetchFeatureFlags(client).then(
        (liste) => ({ liste, erreur: null as string | null }),
        (echec: unknown) => ({
          liste: null,
          erreur: echec instanceof ApiError ? echec.messageKey : 'errors.server',
        }),
      ),
    ]);
    setParametres(valeurs.liste);
    setErreurParametres(valeurs.erreur);
    setDrapeaux(produits.liste);
    setErreurDrapeaux(produits.erreur);
  }, [client]);

  useEffect(() => {
    void relire();
  }, [relire]);

  /** Les domaines servis, dans l'ordre alphabétique. */
  const domaines = useMemo(
    () => [...new Set((parametres ?? []).map((valeur) => valeur.domain))].sort(),
    [parametres],
  );

  const visibles = useMemo(
    () =>
      domaine === null
        ? (parametres ?? [])
        : (parametres ?? []).filter((valeur) => valeur.domain === domaine),
    [domaine, parametres],
  );

  const champsDeBascule: readonly FormFieldDescriptor[] = useMemo(
    () => [
      {
        name: 'enabled',
        labelKey: 'systemSettings.fields.state',
        control: 'select',
        placeholderKey: 'systemSettings.fields.statePlaceholder',
        required: true,
        options: [
          { value: 'true', label: t('systemSettings.fields.open') },
          { value: 'false', label: t('systemSettings.fields.closed') },
        ],
      },
      {
        name: 'rolloutPercentage',
        labelKey: 'systemSettings.fields.rollout',
        control: 'text',
        placeholderKey: 'systemSettings.fields.rolloutPlaceholder',
      },
      {
        name: 'reason',
        labelKey: 'systemSettings.fields.reason',
        control: 'text',
        placeholderKey: 'systemSettings.fields.reasonPlaceholder',
        required: true,
      },
    ],
    [t],
  );

  const basculer = useCallback(
    async (valeurs: Readonly<Record<string, string>>): Promise<void> => {
      if (bascule === null) return;
      try {
        const ouvert = valeurs.enabled === 'true';
        const issue = await switchFeatureFlag(client, {
          code: bascule.code,
          enabled: ouvert,
          rolloutPercentage:
            ouvert && valeurs.rolloutPercentage !== undefined
              && valeurs.rolloutPercentage !== ''
              ? Number(valeurs.rolloutPercentage)
              : undefined,
          reason: valeurs.reason ?? '',
        });
        setErreurGeste(null);
        setBascule(null);
        // **`outcome` est rendu tel quel.** L'action est en double validation :
        // le socle rend 202 avec l'identifiant de la demande, et annoncer un
        // produit ouvert serait annoncer une mise en service qui n'a pas eu
        // lieu — le défaut même que cette action a porté pendant des mois.
        setAvis(
          issue.outcome === 'EXECUTED'
            ? issue.detail
            : t('systemSettings.pending', { id: issue.approvalRequestId ?? '' }),
        );
        await relire();
      } catch (echec) {
        setErreurGeste(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [bascule, client, relire, t],
  );

  return (
    <section aria-label={t('systemSettings.landmark')} className="system-settings">
      <p className="system-settings__journal-notice">
        {t('systemSettings.journalNotice')}
      </p>

      {avis === null ? null : (
        <p className="system-settings__avis" role="status">
          {avis}
        </p>
      )}

      {/* --- Les drapeaux de produit --- */}
      <section
        aria-label={t('systemSettings.flagsTitle')}
        className="system-settings__flags"
      >
        <h2 className="system-settings__section-title">{t('systemSettings.flagsTitle')}</h2>
        {erreurDrapeaux !== null ? (
          <p className="system-settings__notice" role="alert">
            {t(erreurDrapeaux)}
          </p>
        ) : drapeaux === null ? (
          <p className="system-settings__idle">{t('systemSettings.loading')}</p>
        ) : (
          <ul className="system-settings__flag-list" data-testid="system-settings-flags">
            {drapeaux.map((drapeau) => (
              <li className="system-settings__flag" data-testid={`flag-${drapeau.code}`} key={drapeau.code}>
                <span className="system-settings__flag-name">{drapeau.description}</span>
                <span className="system-settings__flag-code">{drapeau.code}</span>
                {/* **La part accompagne TOUJOURS l'état.** « Ouvert » sans sa
                    part ferait croire à une mise en service générale là où
                    trois clients sur cent la reçoivent. */}
                <span
                  className="system-settings__flag-state"
                  data-tone={drapeau.enabled ? 'success' : undefined}
                >
                  {drapeau.enabled
                    ? t('systemSettings.flagOpen', { rollout: drapeau.rolloutPercentage })
                    : t('systemSettings.flagClosed')}
                </span>
                <button
                  className="system-settings__flag-switch"
                  onClick={() => {
                    setBascule(bascule?.code === drapeau.code ? null : drapeau);
                    setErreurGeste(null);
                  }}
                  type="button"
                >
                  {t('systemSettings.switch')}
                </button>
              </li>
            ))}
          </ul>
        )}

        {bascule === null ? null : (
          <div className="system-settings__switch-form" data-testid="system-settings-switch">
            <h3 className="system-settings__section-title">
              {t('systemSettings.switchTitle', { code: bascule.code })}
            </h3>
            <p className="system-settings__meta">{t('systemSettings.switchBody')}</p>
            {erreurGeste === null ? null : (
              <p className="system-settings__notice" role="alert">
                {t(erreurGeste)}
              </p>
            )}
            <FormShell
              fields={champsDeBascule}
              layout="stacked"
              onSubmit={basculer}
              submitLabelKey="systemSettings.switchSubmit"
            />
          </div>
        )}
      </section>

      {/* --- Les paramètres système --- */}
      <section
        aria-label={t('systemSettings.parametersTitle')}
        className="system-settings__parameters"
      >
        <h2 className="system-settings__section-title">
          {t('systemSettings.parametersTitle')}
        </h2>
        <p className="system-settings__meta">{t('systemSettings.parametersReadOnly')}</p>

        {erreurParametres !== null ? (
          <p className="system-settings__notice" role="alert">
            {t(erreurParametres)}
          </p>
        ) : parametres === null ? (
          <p className="system-settings__idle">{t('systemSettings.loading')}</p>
        ) : parametres.length === 0 ? (
          <SystemState
            bodyKey="systemSettings.parametersEmptyBody"
            testId="system-settings-empty"
            titleKey="systemSettings.parametersEmptyTitle"
          />
        ) : (
          <>
            <div className="system-settings__domains" role="group"
                 aria-label={t('systemSettings.domainsLabel')}>
              <button
                className="system-settings__domain"
                data-chosen={domaine === null}
                onClick={() => {
                  setDomaine(null);
                }}
                type="button"
              >
                {t('systemSettings.allDomains', { count: parametres.length })}
              </button>
              {domaines.map((nom) => (
                <button
                  className="system-settings__domain"
                  data-chosen={domaine === nom}
                  data-testid={`domain-${nom}`}
                  key={nom}
                  onClick={() => {
                    setDomaine(nom);
                  }}
                  type="button"
                >
                  {nom}
                </button>
              ))}
            </div>

            <p className="system-settings__meta" data-testid="system-settings-count">
              {t('systemSettings.count', {
                shown: visibles.length,
                total: parametres.length,
              })}
            </p>

            <div className="system-settings__table-frame">
              <table className="system-settings__table" data-testid="system-settings-table">
                <thead>
                  <tr>
                    <th scope="col">{t('systemSettings.columns.key')}</th>
                    <th scope="col">{t('systemSettings.columns.value')}</th>
                    <th scope="col">{t('systemSettings.columns.bounds')}</th>
                    <th scope="col">{t('systemSettings.columns.updated')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((valeur) => (
                    <tr data-testid={`parameter-${valeur.key}`} key={valeur.key}>
                      <th className="system-settings__key-cell" scope="row">
                        <span className="system-settings__key">{valeur.key}</span>
                        <span className="system-settings__description">
                          {valeur.description}
                        </span>
                      </th>
                      <td data-emphasis="numeric">{valeur.value}</td>
                      {/* **Une borne absente n'est PAS zéro.** C'est sur ces
                          bornes qu'un exploitant décide s'il peut changer une
                          valeur sans risque. */}
                      <td data-emphasis="numeric">
                        {valeur.minValue === null && valeur.maxValue === null
                          ? t('systemSettings.noBounds')
                          : t('systemSettings.bounds', {
                              min: valeur.minValue ?? t('systemSettings.noMin'),
                              max: valeur.maxValue ?? t('systemSettings.noMax'),
                            })}
                      </td>
                      <td>
                        <span className="system-settings__description">
                          {t('systemSettings.updatedBy', {
                            by: valeur.updatedBy,
                            at: valeur.updatedAt,
                          })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </section>
  );
}

/** Un état de zone. Voir `ReportingScreen` pour ce que trois `<p>` gris coûtent. */
function SystemState({
  bodyKey,
  testId,
  titleKey,
}: {
  readonly bodyKey: string;
  readonly testId: string;
  readonly titleKey: string;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="system-settings__state" data-testid={testId}>
      <span aria-hidden="true" className="system-settings__state-emblem" />
      <p className="system-settings__state-title">{t(titleKey)}</p>
      <p className="system-settings__state-body">{t(bodyKey)}</p>
    </div>
  );
}
